import React from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Point } from '@visx/point';
import { Line, LineRadial } from '@visx/shape';
import { Chip } from '@mui/material';

const graphFill = '#3BC75E';
const graphStroke = '#2EB450';
const lineColor = '#9BB3C5';
const background = 'transparent';

const chipText = { 0: 'red', 1: '#95BC4C', 2: '#FFF', 3: '#FFF' };
const chipBg = { 0: '#FFF', 1: '#D0F38B', 2: '#F99407', 3: '#3BC75E' };

const y = (d) => Number(d.frequency ?? d.value ?? 0);

/** i번째 축 각도(라디안) — 0번 축이 12시 방향 */
const axisAngleRad = (index, count) =>
  (index * 2 * Math.PI) / count - Math.PI / 2;

/** 중대재해 처벌법 레이더 — 데이터 없을 때 기본 골격(현행 8대분류) */
export const SERIOUS_RADAR_EMPTY_DATA = [
  { subject: '경영자 안전보건경영활동(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '위험성 평가(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '안전보건교육(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '근로자 참여(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '중대재해 대응조치(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '도급.용역.위탁 안전보건확보(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '평가 및 개선(0)', value: 0, fullMark: 100, level: 0 },
  { subject: '안전문화 및 안전의식(0)', value: 0, fullMark: 100, level: 0 },
];

const genWebAngles = (count) =>
  [...new Array(count + 1)].map((_, i) => ({
    angle: axisAngleRad(i % count, count),
  }));

const genAxisPoints = (count, radius) =>
  [...new Array(count)].map((_, i) => {
    const angle = axisAngleRad(i, count);
    return { x: radius * Math.cos(angle), y: radius * Math.sin(angle), angle };
  });

function genPolygonPoints(dataArray, scale, getValue) {
  const count = dataArray.length;
  const pointString = dataArray.reduce((res, d, i) => {
    const value = getValue(d);
    const angle = axisAngleRad(i, count);
    const xVal = scale(value) * Math.cos(angle);
    const yVal = scale(value) * Math.sin(angle);
    return `${res + xVal},${yVal} `;
  }, '');
  return { pointString };
}

/** 항목 수에 따라 라벨·플롯 반경 조정 */
const getLayoutByCount = (count) => {
  if (count <= 8) {
    return {
      plotScale: 1,
      labelPad: 24,
      foWidth: 196,
      foHeight: 34,
      fontSize: '12px',
      chipHeight: 22,
    };
  }
  if (count <= 10) {
    return {
      plotScale: 0.9,
      labelPad: 18,
      foWidth: 164,
      foHeight: 30,
      fontSize: '11px',
      chipHeight: 20,
    };
  }
  return {
    plotScale: 0.8,
    labelPad: 14,
    foWidth: 136,
    foHeight: 26,
    fontSize: '10px',
    chipHeight: 18,
  };
};

const defaultMargin = { top: 50, left: 50, right: 50, bottom: 50 };

export default function RadarChart({
  data = [],
  levels = 4,
  margin = defaultMargin,
  /** true면 데이터 없을 때 0점 골격 표시 (중대재해 대시보드) */
  showEmptySkeleton = true,
}) {
  const isEmptyPlaceholder = !data?.length;
  const displayData = !isEmptyPlaceholder
    ? data
    : showEmptySkeleton
      ? SERIOUS_RADAR_EMPTY_DATA
      : [];

  const baseWidth = 400;
  const baseHeight = 400;
  const count = displayData.length;

  if (count === 0) {
    return (
      <svg
        viewBox={`0 0 ${baseWidth} ${baseHeight}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }

  const layout = getLayoutByCount(count);
  const xMax = baseWidth - margin.left - margin.right;
  const yMax = baseHeight - margin.top - margin.bottom;
  const radius = (Math.min(xMax, yMax) / 2) * layout.plotScale;

  const yScale = scaleLinear({
    range: [0, radius],
    domain: [0, 100],
  });

  const webs = genWebAngles(count);
  const axisPoints = genAxisPoints(count, radius);
  const polygonPoints = genPolygonPoints(
    displayData,
    (d) => yScale(d) ?? 0,
    y
  );
  const zeroPoint = new Point({ x: 0, y: 0 });

  return (
    <svg
      viewBox={`0 0 ${baseWidth} ${baseHeight}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect fill={background} width={baseWidth} height={baseHeight} rx={14} />
      <Group top={baseHeight / 2} left={baseWidth / 2}>
        {[...new Array(levels)].map((_, i) => (
          <LineRadial
            key={`web-${i}`}
            data={webs}
            angle={(d) => d.angle}
            radius={((i + 1) * radius) / levels}
            fill="none"
            stroke={lineColor}
            strokeWidth={1}
            strokeOpacity={0.8}
          />
        ))}
        {axisPoints.map((point, i) => (
          <Line
            key={`radar-line-${i}`}
            from={zeroPoint}
            to={point}
            stroke={lineColor}
          />
        ))}
        <polygon
          points={polygonPoints.pointString}
          fill={graphFill}
          fillOpacity={isEmptyPlaceholder ? 0.2 : 0.47}
          stroke={graphStroke}
          strokeWidth={1}
        />
        {axisPoints.map((point, i) => {
          const item = displayData[i];
          const strSplit = item.subject.match(/(.*)\((\d+(?:\.\d+)?)\)/);
          if (!strSplit) return null;

          const content = strSplit[1].trim();
          const score = item.rawValue ?? strSplit[2];
          const labelR = radius + layout.labelPad;
          const lx = labelR * Math.cos(point.angle);
          const ly = labelR * Math.sin(point.angle);

          return (
            <foreignObject
              key={`tick-${i}`}
              x={lx - layout.foWidth / 2}
              y={ly - layout.foHeight / 2}
              width={layout.foWidth}
              height={layout.foHeight}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                }}
              >
                <span
                  style={{
                    fontWeight: 500,
                    color: '#555',
                    fontSize: layout.fontSize,
                  }}
                >
                  {content}
                </span>
                <Chip
                  label={score}
                  size="small"
                  sx={{
                    ml: '4px',
                    bgcolor: chipBg[item.level],
                    color: chipText[item.level],
                    fontSize: layout.fontSize,
                    fontWeight: 'bold',
                    height: layout.chipHeight,
                  }}
                />
              </div>
            </foreignObject>
          );
        })}
      </Group>
    </svg>
  );
}
